import { NavigateFunction } from 'react-router-dom';
import { InvokeConfig, MachineConfig, StateSchema } from 'xstate';
import API from 'api/API';
import { DraftStatus, ModelStatus, SATOStatus, SetDraftResponse } from 'api/Responses/SetResponses';
import { UseUserState } from 'store/user/useUser';
import { approverRoles, reviewerRoles, userHasPermissions } from 'utils/helpers/permissions.helpers';
import * as Functions from '../../SetPage.functions';

export type TStates = keyof Pick<typeof DraftStatus, 'DRAFT' | 'REVIEW' | 'PENDING' | 'MERGED'>;

/**
 * A few DraftStates are equivalent in the workflow. This function returns the equivalent state.
 * @param status
 * @returns
 */
const mapDraftStatusToMachineState = (status: DraftStatus): TStates => {
  switch (status) {
    case 'DRAFT':
    case DraftStatus.UNTOUCHED:
    case DraftStatus.MODIFIED:
      return 'DRAFT';
    case DraftStatus.REVIEW:
      return 'REVIEW';
    case 'PENDING':
      return 'PENDING';
    case 'MERGED':
    case DraftStatus.VALIDATED:
      return 'MERGED';
    default:
      return 'DRAFT';
  }
};

export type TContext = {
  currentUser: UseUserState;
  setDraft: SetDraftResponse;
};

export interface TStateSchema extends StateSchema {
  meta?: any;
  context?: Partial<TContext>;
  states: {
    DRAFT: {
      states: {
        IDLE: TStateSchema;
        ADDING: TStateSchema;
        SAVING: TStateSchema;
        SUBMITTING: TStateSchema;
        EDITING: TStateSchema;
      };
    };
    REVIEW: {
      states: {
        IDLE: TStateSchema;
        REJECTING: TStateSchema;
        APPROVING: TStateSchema;
        MERGING: TStateSchema;
        EDITING: TStateSchema;
      };
    };
    PENDING: {
      states: {
        IDLE: TStateSchema;
        REJECTING: TStateSchema;
        MERGING: TStateSchema;
        EDITING: TStateSchema;
      };
    };
    MERGED: TStateSchema;
    DELETING: {
      states: {
        IDLE: TStateSchema;
        DELETING: TStateSchema;
      };
    };
  };
}
type EventType =
  | 'ADD_TRAY'
  | 'CONFIRM_ADD_TRAY'
  | 'CONFIRM_EDIT_DRAFT'
  | 'SAVE_DRAFT'
  | 'DELETE'
  | 'CONFIRM_DELETE'
  | 'CANCEL_DELETE'
  | 'SUBMIT_DRAFT'
  | 'REJECT_DRAFT_REVIEW'
  | 'APPROVE_DRAFT_REVIEW'
  | 'MERGE_DRAFT_REVIEW'
  | 'REJECT_DRAFT_PENDING'
  | 'MERGE_DRAFT_PENDING'
  | 'EDIT_DRAFT';

type TEvent = { type: EventType; set: SetDraftResponse };

type TConfig = MachineConfig<TContext, TStateSchema, TEvent>;

const noLiveModel = (setDraft: TContext['setDraft']): boolean => {
  return !!setDraft && setDraft.model_status === ModelStatus.NOT_LIVE;
};

const noDataInSATO = (setDraft: TContext['setDraft']): boolean => {
  return !!setDraft && [SATOStatus.NOT_FOUND, SATOStatus.REJECTED, SATOStatus.DRAFT].includes(setDraft.sato_status);
};

const isOrgAdmin = (params: Pick<TContext, 'currentUser'>): boolean => {
  const { currentUser } = params;
  return userHasPermissions(currentUser, reviewerRoles);
};

const customerCanMerge = (params: TContext): boolean => {
  const { setDraft, currentUser } = params;
  return isOrgAdmin({ currentUser }) && noLiveModel(setDraft) && noDataInSATO(setDraft);
};

const isDraftOwner = (params: TContext): boolean => {
  const { setDraft, currentUser } = params;

  return !!setDraft && currentUser.isAuthenticated && currentUser.user_id === setDraft.owner_id;
};

const isSAVIAdmin = (params: Pick<TContext, 'currentUser'>): boolean => {
  const { currentUser } = params;

  return userHasPermissions(currentUser, approverRoles);
};

const canApproveDraft = (params: TContext): boolean =>
  isDraftOwner(params) && isOrgAdmin(params) && !customerCanMerge(params);

const canMergeDraft = (params: TContext): boolean =>
  (isDraftOwner(params) && customerCanMerge(params)) || isSAVIAdmin(params);

const canSubmitDraft = (params: TContext): boolean =>
  isDraftOwner(params) && !(isOrgAdmin(params) || isSAVIAdmin(params));

const isDraftOwnerOrAdmin = (params: TContext): boolean => {
  const { setDraft, currentUser } = params;

  return (
    !!setDraft &&
    ((currentUser.isAuthenticated && currentUser.user_id === setDraft.owner_id) || isOrgAdmin({ currentUser }))
  );
};

const generateOnError: (verb: string) => InvokeConfig<TContext, { type: 'error' }>['onError'] = verb => ({
  target: 'IDLE',
  actions: (_context, event) => {
    API.errorHandler(new Error(`Error ${verb} draft: ${event.data.toString().replace('Error: ', '')}`));
  },
});

export const createApprovalWorkflowMachineConfig = (params: TContext, navigate: NavigateFunction): TConfig => {
  return {
    id: 'approvalWorkflowMachine',
    initial: mapDraftStatusToMachineState(params.setDraft.status),
    context: params,
    states: {
      DRAFT: {
        initial: 'IDLE',
        states: {
          IDLE: {
            on: {
              ADD_TRAY: {
                cond: isDraftOwner,
                target: 'ADDING',
              },
              SAVE_DRAFT: {
                cond: isDraftOwner,
                target: 'SAVING',
              },
              DELETE: {
                cond: isDraftOwnerOrAdmin,
                target: '#approvalWorkflowMachine.DELETING',
              },
              SUBMIT_DRAFT: {
                cond: canSubmitDraft,
                target: 'SUBMITTING',
              },
              APPROVE_DRAFT_REVIEW: {
                cond: canApproveDraft,
                target: '#approvalWorkflowMachine.REVIEW.APPROVING',
              },
              MERGE_DRAFT_REVIEW: {
                cond: canMergeDraft,
                target: '#approvalWorkflowMachine.REVIEW.MERGING',
              },
              EDIT_DRAFT: {
                target: 'EDITING',
              },
            },
          },
          ADDING: {
            on: {
              CONFIRM_ADD_TRAY: {
                cond: isDraftOwner,
                target: 'IDLE',
              },
            },
          },
          SAVING: {
            invoke: {
              id: 'saveDraft',
              src: async (_ctx: TContext, event) => {
                await Functions.setActionSwitch('save', event.set);
              },
              onDone: { target: 'IDLE' },
              onError: generateOnError('saving'),
            },
          },
          SUBMITTING: {
            invoke: {
              id: 'submitDraft',
              src: async (_ctx: TContext, event) => {
                await Functions.setActionSwitch('submit', event.set);
              },
              onDone: {
                target: '#approvalWorkflowMachine.REVIEW',
                actions: () => navigate('/drafts'),
              },
              onError: generateOnError('submitting'),
            },
          },
          EDITING: {
            on: {
              CONFIRM_EDIT_DRAFT: {
                target: 'IDLE',
              },
            },
          },
        },
      },
      REVIEW: {
        initial: 'IDLE',
        states: {
          IDLE: {
            on: {
              REJECT_DRAFT_REVIEW: {
                cond: isOrgAdmin,
                target: 'REJECTING',
              },
              APPROVE_DRAFT_REVIEW: {
                cond: isOrgAdmin,
                target: 'APPROVING',
              },
              MERGE_DRAFT_REVIEW: {
                cond: customerCanMerge,
                target: 'MERGING',
              },
              DELETE: {
                cond: isDraftOwnerOrAdmin,
                target: '#approvalWorkflowMachine.DELETING',
              },
              EDIT_DRAFT: {
                target: 'EDITING',
              },
            },
          },
          APPROVING: {
            invoke: {
              id: 'approveDraft',
              src: async (_ctx: TContext, event) => {
                await Functions.setActionSwitch('approve', event.set);
              },
              onDone: { target: '#approvalWorkflowMachine.PENDING', actions: () => navigate('/drafts') },
              onError: generateOnError('approving'),
            },
          },
          REJECTING: {
            invoke: {
              id: 'rejectDraft',
              src: async (ctx: TContext) => {
                await Functions.setActionSwitch('rejectReview', ctx.setDraft);
              },
              onDone: { target: '#approvalWorkflowMachine.DRAFT', actions: () => navigate('/drafts') },
              onError: generateOnError('rejecting'),
            },
          },
          MERGING: {
            invoke: {
              id: 'mergeDraft',
              src: async (_ctx: TContext, event) => {
                await Functions.setActionSwitch('merge', event.set);
              },
              onDone: { target: '#approvalWorkflowMachine.MERGED', actions: () => navigate('/drafts') },
              onError: generateOnError('merging'),
            },
          },
          EDITING: {
            on: {
              CONFIRM_EDIT_DRAFT: {
                target: 'IDLE',
              },
            },
          },
        },
      },
      PENDING: {
        initial: 'IDLE',
        states: {
          IDLE: {
            on: {
              REJECT_DRAFT_PENDING: {
                cond: isSAVIAdmin,
                target: 'REJECTING',
              },
              MERGE_DRAFT_PENDING: {
                cond: isSAVIAdmin,
                target: 'MERGING',
              },
              DELETE: {
                cond: isDraftOwnerOrAdmin,
                target: '#approvalWorkflowMachine.DELETING',
              },
              EDIT_DRAFT: {
                target: 'EDITING',
              },
            },
          },
          REJECTING: {
            invoke: {
              id: 'rejectDraftPending',
              src: async (ctx: TContext) => {
                await Functions.setActionSwitch('rejectPending', ctx.setDraft);
              },
              onDone: { target: '#approvalWorkflowMachine.DRAFT', actions: () => navigate('/drafts') },
              onError: generateOnError('rejecting'),
            },
          },
          MERGING: {
            invoke: {
              id: 'mergeDraftPending',
              src: async (_ctx: TContext, event) => {
                await Functions.setActionSwitch('merge', event.set);
              },
              onDone: { target: '#approvalWorkflowMachine.MERGED', actions: () => navigate('/drafts') },
              onError: generateOnError('merging'),
            },
          },
          EDITING: {
            on: {
              CONFIRM_EDIT_DRAFT: {
                target: 'IDLE',
              },
            },
          },
        },
      },
      DELETING: {
        initial: 'IDLE',
        states: {
          IDLE: {
            on: {
              CONFIRM_DELETE: {
                cond: isDraftOwnerOrAdmin,
                target: 'DELETING',
              },
              CANCEL_DELETE: {
                cond: isDraftOwnerOrAdmin,
                target: '#approvalWorkflowMachine.DRAFT',
              },
            },
          },
          DELETING: {
            invoke: {
              id: 'deleteDraft',
              src: async (ctx: TContext) => {
                await Functions.setActionSwitch('delete', ctx.setDraft);
              },
              onDone: {
                target: '#approvalWorkflowMachine.DRAFT',
                actions: () => navigate('/drafts'),
              },
              onError: generateOnError('deleting'),
            },
          },
        },
      },
      MERGED: {
        type: 'final',
      },
    },
  };
};

export const helpers = {
  noLiveModel,
  noDataInSATO,
  isDraftOwner,
  isDraftOwnerOrAdmin,
  isOrgAdmin,
  isSAVIAdmin,
  customerCanMerge,
  mapDraftStatusToMachineState,
  canApproveDraft,
  canMergeDraft,
  canSubmitDraft,
};
